import { AppBar, Box, Typography } from "@mui/material";
import React, { Suspense, useCallback, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Loading from "@components/common/Loading";
import { PATHS } from "@config";
import { checkPathsList } from "@config/pathsList";
import { QUERY_KEY } from "@config/queryKeys";
import { logout } from "@redux/auth/actions";
import { showJobs } from "@redux/jobs/actions";
import { getMstConfig } from "@services/mst";
import {
  checkStaff,
  getSumajobStaffDetail,
  updateStaffShowNotification,
} from "@services/sumajob_staff";
import { checkExistPage, checkExistPageWithPath } from "@utils/checkFunc";
import queryClient from "@config/queryClient";
import {
  checkRegisterProcess,
  getForeignerRegisterUrl,
  IS_REGISTER_PROCESS_QUERY_STRING,
} from "@utils/checkRegisterProcess";
import { CONFIRM_PUSH_NOTIFICATION } from "@config/storageKey";
import ModalBase from "@components/Job/Modal/ModalBase";
import { handleShowErrorPopup } from "@utils/handleShowErrorPopup";
import { useLoading } from "@contexts/LoadingContext";
import { requestNotificationPermission } from "@pages/Settings/Notification/helper";
import Footer from "./Footer";
import Header from "./Header";

const LayoutAuth = () => {
  const { user } = useSelector((state) => state.auth);
  const { showListOffer } = useSelector((state) => state.jobs);
  const dispatchHiddenJobs = useCallback(() => dispatch(showJobs({ hasShow: false })), []);
  const { isRegisterProcess } = checkRegisterProcess();

  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [footerUI, setFooterUI] = useState(null);
  const [hasUnread, setHasUnread] = useState(false);
  const [showPopupComeback, setShowPopupComeback] = useState(false);
  const [isOpenConfirmNotification, setIsOpenConfirmNotification] = useState(false);
  const { showLoading, hideLoading } = useLoading();

  useEffect(() => {
    if (user?.access_token) {
      Swal.close(); // close alert
      fetchData();
    }
    // get mst config
    checkExistPage(checkPathsList.listPageGetMstConfig, pathname) && refetchMstConfig();
    showListOffer &&
      !checkExistPage(checkPathsList.listPageShowJob, pathname) &&
      dispatchHiddenJobs();
    // remove cache jobs
    if (!checkExistPage(checkPathsList.listPageShowJob, pathname)) {
      queryClient.setQueryData(QUERY_KEY.JOBS, null);
    }
  }, [pathname, user?.access_token]);

  const checkPushNotification = async (showNotification = false, hasNotification = false) => {
    const confirmPushNotification = localStorage.getItem(CONFIRM_PUSH_NOTIFICATION);
    if (!confirmPushNotification) {
      await requestNotificationPermission(hasNotification, false, false);
      if (
        showNotification &&
        !checkExistPageWithPath(checkPathsList.listPageNotShowNotificationModal, pathname)
      ) {
        setIsOpenConfirmNotification(true);
      }
    }
  };

  const onHandleConfirmNotification = async () => {
    try {
      showLoading();
      localStorage.setItem(CONFIRM_PUSH_NOTIFICATION, true);
      setIsOpenConfirmNotification(false);
      await updateStaffShowNotification();
      hideLoading();
      navigate(PATHS.SETTING_NOTIFICATION);
    } catch (err) {
      handleShowErrorPopup(err?.message);
    }
  };

  const fetchData = () => {
    refetchCheckStaff(); // refetch check staff
    // get staff info
    refetchGetSumajobStaffDetail();
  };

  const { refetch: refetchCheckStaff } = useQuery({
    queryFn: () => checkStaff(),
    queryKey: `${QUERY_KEY.CHECK_STAFF}`,
    enabled: false,
    onSuccess: (res) => {
      if (res.success) {
        const { data } = res;
        if (!data.result) {
          dispatch(logout());
        } else {
          setHasUnread(data.has_unread);
          setShowPopupComeback(data.show_popup_comeback);
        }
      }
    },
  });

  const { isLoading: loadingMstConfig, refetch: refetchMstConfig } = useQuery({
    queryFn: getMstConfig,
    queryKey: QUERY_KEY.MST_CONFIG,
    enabled: false,
  });

  const {
    data: staffDetail,
    refetch: refetchGetSumajobStaffDetail,
    isLoading: loadingSumaStaff,
  } = useQuery({
    queryFn: () => getSumajobStaffDetail(),
    queryKey: QUERY_KEY.SUMAJOB_STAFF,
    enabled: false,
    onSuccess: (res) => {
      if (res.success) {
        const { data } = res;
        if (!data?.casnavi_id && !isRegisterProcess && data?.is_foreigner && !data?.is_send_mail) {
          const newUrl = getForeignerRegisterUrl(Boolean(data?.confirm_date));
          navigate(`${newUrl}${IS_REGISTER_PROCESS_QUERY_STRING}`);
        }
        checkPushNotification(data?.show_notification, data?.has_notification);
      }
    },
  });

  useEffect(() => {
    if (checkExistPage(checkPathsList.listPagesCustomFooter, pathname)) {
      setFooterUI(null);
    } else {
      setFooterUI(
        <Footer
          hasUnread={hasUnread}
          isDisable={
            !staffDetail?.data?.casnavi_id &&
            staffDetail?.data?.is_foreigner &&
            !staffDetail?.data?.is_send_mail
          }
        />
      );
    }
  }, [pathname, hasUnread, staffDetail]);

  const checkLoading = loadingMstConfig || loadingSumaStaff;

  return (
    <div className="container-fluid jvs-p-t15 px-0">
      <Header staffDetail={staffDetail} />
      <ModalBase
        title="<b>“スマジョブ”は通知を送信します。</b>"
        buttonText="設定する"
        open={isOpenConfirmNotification}
        handleSuccess={onHandleConfirmNotification}
        showButtonCancel={false}
      >
        <Box sx={{ textAlign: "center" }}>
          <Typography sx={{ fontSize: "16px" }}>よろしいですか？</Typography>
        </Box>
      </ModalBase>
      {!checkLoading && showPopupComeback && (
        <ModalBase
          buttonText="お仕事検索へ"
          cancelText="閉じる"
          open={showPopupComeback}
          handleClose={() => setShowPopupComeback(false)}
          handleSuccess={() => {
            setShowPopupComeback(false);
            navigate(PATHS.HOME);
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Typography sx={{ fontSize: "16px" }}>
              【おかえりやすポイント！！】
              <br />
              今だけ！3か月ぶりにお仕事を再開する方に、1,000ptプレゼント！
              <br />
              あなたも対象ですので、ぜひこの機会にご応募くださいね。お待ちしております！
            </Typography>
          </Box>
        </ModalBase>
      )}
      <main>
        <Suspense fallback={<Loading loading />}>
          {checkLoading ? <Loading loading /> : <Outlet context={{ setHasUnread }} />}
        </Suspense>
      </main>
      {footerUI && <AppBar className="jvs-footer-bar jvs-bg-blue">{footerUI}</AppBar>}
    </div>
  );
};

export default LayoutAuth;
